/**
 * Person pages live at /people/<email> (the directory keys people by email),
 * so a lead's address — third-party PII — rode into analytics and error
 * tracking in every URL. Telemetry hooks run their URLs through this.
 */
// The domain must end in a letters-only TLD, so `clerk-js@6.41.0` and the
// `cleanlist-portal@1.2.3` build id are left alone: redacting them turned
// Datadog's version tag into `_email_`.
const EMAIL_IN_TEXT = /[^\s/?#&=:]+(?:@|%40)[^\s/?#&=]*\.[a-z]{2,}(?![\w-])/gi;

export const redactEmails = (text: string): string =>
  text.replace(EMAIL_IN_TEXT, '[email]');

/** URL-ish string properties of a PostHog event ($current_url, $pathname,
 * $referrer, the $initial_* copies in $set_once...). */
const isUrlProperty = (key: string) => /url|pathname|referrer/i.test(key);

const redactProperties = (props: Record<string, unknown> | undefined) => {
  if (!props) return;
  for (const [key, value] of Object.entries(props)) {
    if (typeof value === 'string' && isUrlProperty(key)) {
      props[key] = redactEmails(value);
    }
  }
};

/** PostHog `before_send`: the event with emails stripped from its URLs. */
export const redactPosthogEvent = <
  T extends {
    properties?: Record<string, unknown>;
    $set?: Record<string, unknown>;
    $set_once?: Record<string, unknown>;
  },
>(
  event: T | null
): T | null => {
  if (!event) return event;
  redactProperties(event.properties);
  redactProperties(event.$set);
  redactProperties(event.$set_once);
  return event;
};

/** Link-token params Clu's Slack and Teams links carry (query or fragment). */
const LINK_TOKEN_PARAM =
  /([?&#])(teams_connect|teams_link|slack_link)=[^&#]*/gi;
/** Endpoints whose request body is a signed link token. */
const LINK_TOKEN_ENDPOINT =
  /\/integrations\/(?:teams\/bot\/(?:connect|link)|slack\/link)(?:\/[a-z]+)?(?:[?#]|$)/i;

/** PostHog replay `maskCapturedNetworkRequestFn`: no link tokens, no lead
 * emails. Covers the page-load entry, which keeps the URL the browser opened
 * before the token was scrubbed from the address bar. */
export const maskPosthogNetworkRequest = <
  T extends {
    name: string;
    requestBody?: string | null;
    responseBody?: string | null;
  },
>(
  request: T
): T => {
  const masked = { ...request };
  const url = request.name ?? '';
  if (LINK_TOKEN_ENDPOINT.test(url)) {
    masked.requestBody = null;
    masked.responseBody = null;
  }
  masked.name = redactEmails(url.replace(LINK_TOKEN_PARAM, '$1$2=[redacted]'));
  return masked;
};
